import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loader/Loader";
import { getHiddenStamps } from "../../utilities/stamps-service";
import StampItem from "../StampsIndexPage/StampItem";

const PAGE_SIZE = 30;

export default function HiddenStampsPage({ user }) {
  const navigate = useNavigate();
  const [stamps, setStamps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const sentinelRef = useRef(null);

  const fetchPage = useCallback(async (pageNum, append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    const params = new URLSearchParams();
    params.set("page", pageNum);
    params.set("limit", PAGE_SIZE);

    const response = await getHiddenStamps(params);

    if (response && response.stamps) {
      setStamps((prev) =>
        append ? [...prev, ...response.stamps] : response.stamps,
      );
      setHasMore(response.hasMore);
      setPage(pageNum);
    } else {
      console.log("error: ", response);
    }

    if (append) {
      setIsLoadingMore(false);
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  // Intersection Observer for endless scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isLoadingMore
        ) {
          fetchPage(page + 1, true);
        }
      },
      { rootMargin: "400px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, page, fetchPage]);

  if (!user || !user.isAdmin) {
    return (
      <div className="text-center py-20">
        <p className="text-gray-500 mb-4">Not authorised</p>
        <button
          className="text-sm underline text-gray-900"
          onClick={() => navigate("/")}
        >
          Return to home
        </button>
      </div>
    );
  }

  return (
    <section className="w-full bg-neutral-950 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <h1 className="text-2xl font-semibold tracking-tight text-white mb-8">
          Hidden stamps
        </h1>
      </div>

      <div className="w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader />
            </div>
          ) : stamps.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <p className="text-neutral-400 text-base">
                No hidden stamps found.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8">
                {stamps.map((stamp, idx) => (
                  <StampItem key={`${stamp._id}-${idx}`} stamp={stamp} />
                ))}
              </div>
              <div ref={sentinelRef} className="h-1" />
              {isLoadingMore && (
                <div className="flex justify-center items-center py-10">
                  <Loader />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
